import React, { forwardRef, useMemo, useState, useEffect } from 'react'

// types
import {
  IEcommerceAnswerSchema,
  IEcommerceQuestionSchema,
  EcommerceResponse,
  EcommerceBannerPlacement
} from '@aytm/app/swidget/base/questions/Ecommerce/types'

// components
import Rating from './Rating'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink.tsx'
import AddToCartButton from './AddToCartButton'

// functions
import { unescapeTags } from '@aytm/lib/common/func'
import { getMediumAssetPath } from '@aytm/lib/common/assets.ts'
import { formatPriceWithLocale } from '@aytm/app/swidget/base/questions/Ecommerce/utils/formatPriceWithLocale.ts'
import { getDaysAmount } from '@aytm/app/swidget/base/questions/Ecommerce/utils/getDaysAmount.ts'
import { stripHighlightTags } from '../../utils/stripHighlightTags'

// hooks
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale.ts'
import { useBannerImpression } from '../../hooks/useBannerImpression'

// styles
import commonStyle from '../Common.module.scss'
import style from './Card.module.scss'
import Subproducts from './Subproducts'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline'

interface ICardProps {
  answer: IEcommerceAnswerSchema
  questionSchema: IEcommerceQuestionSchema
  index: number
  deliveryDate: number
  disabled?: boolean
  small?: boolean
  response: EcommerceResponse
  findability: boolean
  banner?: boolean
  bannerPlacement?: EcommerceBannerPlacement
  applyCustomWidth?: boolean
  'data-testid'?: string
  onAddToCart: (qnt: 1 | -1, index: number) => void
  onItemSelect: (index: number) => void
}

const Card = forwardRef<HTMLDivElement, ICardProps>(
  (
    {
      answer,
      questionSchema,
      deliveryDate,
      response,
      findability,
      small,
      disabled,
      banner,
      bannerPlacement,
      applyCustomWidth = false,
      'data-testid': dataTestId,
      onAddToCart,
      onItemSelect
    },
    ref
  ) => {
    // custom hooks calls
    const viewport = useViewport()
    const { t, languageTag } = useSurveyLocale()
    const timeline = useTimeline()

    // Track banner impression for featured products. Hook is called unconditionally
    // (React Rules of Hooks); a null `placement` short-circuits inside the hook.
    const bannerRef = useBannerImpression(answer.id, bannerPlacement ?? null)
    const combinedRef = bannerPlacement ? bannerRef : ref

    const [activeAnswer, setActiveAnswer] = useState(answer)
    const [imageLoading, setImageLoading] = useState(false)

    useEffect(() => {
      if (activeAnswer.image?.original_url) {
        setImageLoading(true)
      }
    }, [activeAnswer.id])

    const activeAnswerIndex = useMemo(
      () => questionSchema.answers.findIndex(answer => answer.id === activeAnswer.id),
      [questionSchema.answers, activeAnswer]
    )
    const isDiscounted = activeAnswer.discounted_price > 0 && activeAnswer.price > activeAnswer.discounted_price

    const onSubitemSelect = (index: number) => {
      setActiveAnswer(questionSchema.answers[index])
      timeline.log({
        ref: questionSchema.answers[index].id,
        type: `main_page_select_subproduct`
      })
    }

    // functions
    const getPriorityLabel = () => {
      if (isDiscounted) {
        return <div className={commonStyle.rollback}>{t('questions', 'ec', 'walmart', 'rollback')}</div>
      }
      if (activeAnswer.best_seller) {
        return <div className={commonStyle.best}>{t('questions', 'ec', 'walmart', 'bestSeller')}</div>
      }
      if (activeAnswer.overal_pick) {
        return <div className={commonStyle.bought}>{t('questions', 'ec', 'walmart', 'popularPick')}</div>
      }
      if (activeAnswer.red_label) {
        return <div className={commonStyle.redLabel}>{unescapeTags(activeAnswer.red_label)}</div>
      }
      if (activeAnswer.green_label) {
        return (
          <div className={commonStyle.greenLabel}>{stripHighlightTags(unescapeTags(activeAnswer.green_label))}</div>
        )
      }
      return null
    }

    return (
      <div
        ref={combinedRef}
        data-testid={dataTestId}
        className={`
        ${style.card}
        ${viewport.mobile ? style.cardMobile : ''}
        ${small ? style.small : ''}
        ${banner ? style.banner : ''}
        ${disabled ? style.disabled : ''}
        ${
          applyCustomWidth && null !== questionSchema.products_per_row && !viewport.mobile
            ? style[`card${questionSchema.products_per_row}PerRow`]
            : ''
        }
      `}
      >
        <div className={`${commonStyle.labels} ${banner ? style.bannerLabels : ''}`}>{getPriorityLabel()}</div>
        <div className={style.content}>
          <div className={style.imageWrapper}>
            <div className={style.heart}>
              <EmptyLink color="#0053e2">
                <i className="wm-Heart" />
              </EmptyLink>
            </div>
            {activeAnswer.image && activeAnswer.image.original_url ? (
              <>
                <img
                  onClick={() => onItemSelect(activeAnswerIndex)}
                  data-testid="SW-SH-ECommerceImage"
                  className={style.image}
                  alt={activeAnswer.image?.alt_text || t('questions', 'img', 'noAlt')}
                  src={getMediumAssetPath(activeAnswer.image, 'transparent')}
                  onLoad={() => setImageLoading(false)}
                  style={{ opacity: imageLoading ? 0 : 1, transition: 'opacity 0.2s' }}
                />
                {imageLoading && <div className={style.imageLoader} />}
              </>
            ) : (
              <div className={style.noImage} onClick={() => onItemSelect(activeAnswerIndex)} />
            )}
          </div>
          {questionSchema.grid_variants && !banner && !small && (
            <div className={style.subproductsWrapper}>
              <Subproducts
                answer={activeAnswer}
                schema={questionSchema}
                onSubitemSelect={onSubitemSelect}
                compact={true}
              />
            </div>
          )}
          <div className={style.contentWrapper}>
            <div className={style.button}>
              <AddToCartButton
                width={viewport.mobile ? '100%' : null}
                index={activeAnswerIndex}
                amount={response.answers[activeAnswerIndex]?.v || 0}
                findability={findability}
                limit={questionSchema.max_qty_per_product}
                defaultLabel={
                  viewport.mobile
                    ? t('questions', 'ec', 'walmart', 'addToCart')
                    : t('questions', 'ec', 'walmart', 'add')
                }
                collapsible={true}
                showIcon={!viewport.mobile}
                onAddToCart={onAddToCart}
              />
            </div>
            {small || !answer.amazon_choice ? (
              ''
            ) : (
              <div className={style.note}>
                <span className={style.noteText}>{t('questions', 'ec', 'walmart', 'sponsored')}</span>
                <EmptyLink color="#0053e2">
                  <i className="pi pi-info-circle" />
                </EmptyLink>
              </div>
            )}
            {isDiscounted && (
              <div className={style.discounted}>
                <div className={style.currentPrice}>
                  <span className={`${style.now} ${style.green}`}>{t('questions', 'ec', 'walmart', 'now')}</span>
                  <span
                    className={`${style.price} ${style.green}`}
                    dangerouslySetInnerHTML={{
                      __html: `${formatPriceWithLocale(
                        activeAnswer.discounted_price,
                        questionSchema.price_mask,
                        languageTag,
                        true,
                        !small
                      )}`
                    }}
                  ></span>
                </div>
                {activeAnswer.price > 0 && (
                  <div className={style.lastPrice}>
                    <span
                      className={`${style.priceSingleSize}`}
                      dangerouslySetInnerHTML={{
                        __html: `${formatPriceWithLocale(
                          activeAnswer.price,
                          questionSchema.price_mask,
                          languageTag,
                          true,
                          false
                        )}`
                      }}
                    ></span>
                  </div>
                )}
                {activeAnswer.per_count_price && (
                  <div className={style.perCount}>{unescapeTags(activeAnswer.per_count_price)}</div>
                )}
              </div>
            )}
            {!isDiscounted && activeAnswer.price && (
              <div className={style.regular}>
                <div
                  className={style.price}
                  dangerouslySetInnerHTML={{
                    __html: formatPriceWithLocale(
                      activeAnswer.price,
                      questionSchema.price_mask,
                      languageTag,
                      true,
                      !small
                    )
                  }}
                ></div>
                {activeAnswer.per_count_price && (
                  <div className={style.perCount}>{unescapeTags(activeAnswer.per_count_price)}</div>
                )}
              </div>
            )}

            <div
              className={style.description}
              dangerouslySetInnerHTML={{ __html: answer.text }}
              onClick={() => onItemSelect(activeAnswerIndex)}
            />
            <div className={style.review}>
              <Rating rating={answer.rating} color={viewport.mobile ? '#000' : '#ffc220'} />
              <span
                className={style.reviewsCount}
                dangerouslySetInnerHTML={{ __html: answer.reviews?.toLocaleString() || '' }}
              ></span>
            </div>
            <div
              className={style.shipping}
              dangerouslySetInnerHTML={{
                __html: `<span>Shipping, arrives</span>&nbsp;${t<(days: number) => string>(
                  'questions',
                  'ec',
                  'getDaysLeft'
                )(getDaysAmount(deliveryDate))}`
              }}
            ></div>
          </div>
        </div>
      </div>
    )
  }
)

Card.displayName = 'Card'
export default Card
